import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, UserPlus, MessageCircle, FileText, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { ScrollArea } from './ui/scroll-area';
import { notificationAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';
import { formatRelativeTime, getAvatarFallback } from '../lib/utils';

export function NotificationBell() {
  const { unreadNotifications, fetchUnreadCounts } = useAuth();
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await notificationAPI.list({ limit: 20 });
      setNotifications(res.data.notifications || []);
    } catch (err) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open]);

  const handleMarkAllRead = async () => {
    try {
      await notificationAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      fetchUnreadCounts();
    } catch (err) {
      // ignore
    }
  };

  const handleNotificationClick = (notif) => {
    setOpen(false);
    if (notif.type === 'new_follower') {
      navigate(`/profile/${notif.actor_id}`);
    } else if (notif.type === 'new_message') {
      navigate(`/messages/${notif.reference_id}`);
    } else if (notif.type === 'new_update') {
      navigate(`/profile/${notif.actor_id}`);
    }
  };

  const getNotifIcon = (type) => {
    switch (type) {
      case 'new_follower': return <UserPlus className="w-4 h-4 text-blue-500" />;
      case 'new_message': return <MessageCircle className="w-4 h-4 text-orange-500" />;
      case 'new_update': return <FileText className="w-4 h-4 text-green-500" />;
      default: return <Bell className="w-4 h-4 text-slate-400" />;
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative p-2 text-slate-600 hover:text-slate-900 transition-colors" data-testid="notification-bell">
          <Bell className="w-5 h-5" />
          {unreadNotifications > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1">
              {unreadNotifications > 99 ? '99+' : unreadNotifications}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <h3 className="font-semibold text-slate-900 text-sm">{t('notifications.title')}</h3>
          {unreadNotifications > 0 && (
            <Button variant="ghost" size="sm" onClick={handleMarkAllRead} className="text-xs text-orange-600">
              <Check className="w-3 h-3 mr-1" /> {t('notifications.markAllRead')}
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-80">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" />
            </div>
          ) : notifications.length > 0 ? (
            <div className="divide-y divide-slate-50">
              {notifications.map((notif) => (
                <button
                  key={notif.notification_id}
                  className={`w-full text-left flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition-colors ${
                    !notif.read ? 'bg-orange-50/50' : ''
                  }`}
                  onClick={() => handleNotificationClick(notif)}
                >
                  <Avatar className="w-8 h-8 mt-0.5 flex-shrink-0">
                    <AvatarImage src={notif.actor_picture} />
                    <AvatarFallback className="bg-slate-100 text-slate-600 text-xs">
                      {getAvatarFallback(notif.actor_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      {getNotifIcon(notif.type)}
                      <p className="text-sm text-slate-700 truncate">{notif.message}</p>
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">{formatRelativeTime(notif.created_at)}</p>
                  </div>
                  {!notif.read && (
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          ) : (
            <p className="text-center text-slate-500 py-8 text-sm">{t('notifications.empty')}</p>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
